import type { ICountryDefinition } from "@/countries/country-common";
import type { ECountry, ECountryCode } from "@/domains/country/country.types.ts";

import { BelgiumCountryDefinition } from "@/countries/impl/be.coutry-definition.ts";
import { CzechCountryDefinition } from "@/countries/impl/cz.country-definition.ts";
import { SpainCountryDefinition } from "@/countries/impl/es.country-definition.ts";
import { FinlandCountryDefinition } from "@/countries/impl/fi.country-definition.ts";
import { FranceCountryDefinition } from "@/countries/impl/fr.coutry-definition.ts";
import { GreatBritainCountryDefinition } from "@/countries/impl/gb.country-definition.ts";
import { HungaryCountryDefinition } from "@/countries/impl/hu.country-definition.ts";
import { SwedenCountryDefinition } from "@/countries/impl/se.country-definition.ts";
import { SloveniaCountryDefinition } from "@/countries/impl/si.country-definition.ts";

export interface ICountryDefinitionPhone {
  mask: string;
  placeholder: string;
  prefix: string;
}

export type TCountryDefinitions =
  | typeof FranceCountryDefinition
  | typeof BelgiumCountryDefinition
  | typeof FinlandCountryDefinition
  | typeof SloveniaCountryDefinition
  | typeof SwedenCountryDefinition
  | typeof GreatBritainCountryDefinition
  | typeof SpainCountryDefinition
  | typeof CzechCountryDefinition
  | typeof HungaryCountryDefinition;

export const ALL_COUNTRIES = [
  FranceCountryDefinition,
  BelgiumCountryDefinition,
  FinlandCountryDefinition,
  SloveniaCountryDefinition,
  SwedenCountryDefinition,
  GreatBritainCountryDefinition,
  SpainCountryDefinition,
  CzechCountryDefinition,
  HungaryCountryDefinition,
];

export const ALL_PHONE_ALLOWED_COUNTRIES = ALL_COUNTRIES.filter((country) => country.phoneAllowed);
export const ALL_ADDRESS_ALLOWED_COUNTRIES = ALL_COUNTRIES.filter((country) => country.addressAllowed);

export type TCountryVisitor<TResult = void> = {
  [C in TCountryDefinitions["country"]]: (def: Extract<TCountryDefinitions, { country: C }>) => TResult;
};

export function visitCountry<TResult = void>(def: TCountryDefinitions, visitor: TCountryVisitor<TResult>): TResult {
  return visitor[def.country](def as never) as TResult;
}

export function findCountryDefinitionByCountry(country: ECountry, franceFallback: true): ICountryDefinition;
export function findCountryDefinitionByCountry(
  country: ECountry,
  franceFallback?: false | undefined,
): ICountryDefinition | undefined;
export function findCountryDefinitionByCountry(
  country: ECountry,
  franceFallback: boolean = false,
): ICountryDefinition | undefined {
  for (const countryDefinition of ALL_COUNTRIES) {
    if (countryDefinition.country === country) {
      return countryDefinition;
    }
  }

  if (franceFallback) {
    return FranceCountryDefinition;
  }

  return undefined;
}

export function findCountryDefinitionByCountryIsoCode(countryCode: ECountryCode): ICountryDefinition {
  return ALL_COUNTRIES.find((c) => c.countryCode === countryCode) ?? FranceCountryDefinition;
}

export function findCountryDefinitionByCountryCode(countryCode?: ECountryCode): ICountryDefinition {
  for (const countryDefinition of ALL_COUNTRIES) {
    if (countryDefinition.countryCode === countryCode) {
      return countryDefinition;
    }
  }

  return FranceCountryDefinition;
}
